import { useCallback, useEffect, useRef, useState } from 'react'
import { authFetch } from '../lib/auth'

type ClawdState = 'idle' | 'typing' | 'thinking' | 'working' | 'building' | 'sleeping' | 'error' | 'happy' | 'juggling' | 'sweeping' | 'attention' | 'notification' | 'carrying'

const STATE_TO_GIF: Record<string, string> = {
  idle: '/clawd/clawd-idle.gif',
  typing: '/clawd/clawd-typing.gif',
  thinking: '/clawd/clawd-thinking.gif',
  working: '/clawd/clawd-typing.gif',
  building: '/clawd/clawd-building.gif',
  sleeping: '/clawd/clawd-sleeping.gif',
  error: '/clawd/clawd-error.gif',
  happy: '/clawd/clawd-happy.gif',
  juggling: '/clawd/clawd-typing.gif',
  sweeping: '/clawd/clawd-thinking.gif',
  attention: '/clawd/clawd-idle.gif',
  notification: '/clawd/clawd-happy.gif',
  carrying: '/clawd/clawd-typing.gif',
}

const STATE_LABELS: Record<string, string> = {
  idle: '发呆中~',
  typing: '啪唧啪唧',
  thinking: '在想...',
  working: '啪唧啪唧',
  building: '在建造!',
  sleeping: 'zzZ',
  error: '出错了!',
  happy: '开心~',
  juggling: '在忙...',
  sweeping: '打扫中~',
  attention: '看这边!',
  notification: '叮!',
  carrying: '搬东西~',
}

const SAVED_POS_KEY = 'clawd_pet_position'

function loadPosition(): { x: number; y: number } | null {
  try {
    const s = localStorage.getItem(SAVED_POS_KEY)
    if (!s) return null
    return JSON.parse(s)
  } catch {
    return null
  }
}

function savePosition(x: number, y: number) {
  localStorage.setItem(SAVED_POS_KEY, JSON.stringify({ x, y }))
}

export function ClawdPet() {
  const [state, setState] = useState<ClawdState>('sleeping')
  const [pos, setPos] = useState(() => loadPosition() || { x: window.innerWidth - 100, y: window.innerHeight - 180 })
  const [dragging, setDragging] = useState(false)
  const [, setPokeCount] = useState(0)
  const [showLabel, setShowLabel] = useState(false)
  const [reaction, setReaction] = useState<string | null>(null)
  const dragOffset = useRef({ x: 0, y: 0 })
  const pokeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const labelTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const petRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let mounted = true
    const poll = async () => {
      try {
        const r = await authFetch('/api/clawd-state')
        if (!r.ok) return
        const data = await r.json()
        if (mounted && data.state) {
          setState(data.state as ClawdState)
        }
      } catch { /* ignore */ }
    }
    poll()
    const id = setInterval(poll, 3000)
    return () => { mounted = false; clearInterval(id) }
  }, [])

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!petRef.current) return
    e.preventDefault()
    const rect = petRef.current.getBoundingClientRect()
    dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    setDragging(true)
    petRef.current.setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging) return
    const newX = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragOffset.current.x))
    const newY = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragOffset.current.y))
    setPos({ x: newX, y: newY })
  }, [dragging])

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!dragging) return
    setDragging(false)
    if (petRef.current) petRef.current.releasePointerCapture(e.pointerId)
    const newX = Math.max(0, Math.min(window.innerWidth - 80, e.clientX - dragOffset.current.x))
    const newY = Math.max(0, Math.min(window.innerHeight - 80, e.clientY - dragOffset.current.y))
    savePosition(newX, newY)
  }, [dragging])

  const handleClick = useCallback(() => {
    if (dragging) return
    setPokeCount(prev => {
      const next = prev + 1
      if (next >= 4) {
        setReaction('🫨')
        setTimeout(() => setReaction(null), 1500)
        return 0
      }
      if (next >= 2) {
        setReaction('❗')
        setTimeout(() => setReaction(null), 800)
      }
      return next
    })
    if (pokeTimer.current) clearTimeout(pokeTimer.current)
    pokeTimer.current = setTimeout(() => setPokeCount(0), 2000)

    setShowLabel(true)
    if (labelTimer.current) clearTimeout(labelTimer.current)
    labelTimer.current = setTimeout(() => setShowLabel(false), 3000)
  }, [dragging])

  const gif = STATE_TO_GIF[state] || STATE_TO_GIF.idle
  const label = STATE_LABELS[state] || ''

  return (
    <div
      ref={petRef}
      className="clawd-pet"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 9999,
        touchAction: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        transition: dragging ? 'none' : 'left 0.1s, top 0.1s',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleClick}
    >
      {(showLabel || state === 'sleeping') && (
        <div className="clawd-label">{label}</div>
      )}
      {reaction && (
        <div className="clawd-reaction">{reaction}</div>
      )}
      <img
        src={gif}
        alt="Clawd"
        draggable={false}
        className={`clawd-sprite ${dragging ? 'clawd-dragging' : ''}`}
      />
    </div>
  )
}
